import validationUtils from './validationUtils';
import { BaseEntityUtils } from './BaseEntityUtils';
import phoneUtils, { DriverPhoneUse, PhoneType } from './phoneUtils';
import emailUtils, { DriverEmailUse } from './emailUtils';
import { Driver } from '../data/models/driver.model';
import { DriverPhoneNumber } from '../data/models/phone-number.model';
import { DriverEmailAddress } from '../data/models/email-address.model';
import { DateTime } from 'luxon';

class DriverUtils extends BaseEntityUtils {
  public getDefaults(driver: Driver | null | undefined): any {
    const email = emailUtils.getDriverEmail(driver?.emailAddresses);
    const primaryPhone = phoneUtils.getDriverPrimaryPhone(driver?.phoneNumbers);
    const secondaryPhone = phoneUtils.getDriverSecondaryPhone(
      driver?.phoneNumbers,
    );
    const emergencyPhone = phoneUtils.getDriverEmergencyPhone(
      driver?.phoneNumbers,
    );
    const messagingMethods = driver?.driverMessagingMethods || [];
    const messagingMethod =
      messagingMethods.length > 0
        ? messagingMethods[0].driverMessagingMethodID
        : '';
    return {
      FirstName: this.getDefaultString(driver?.driverFirstName),
      MiddleName: this.getDefaultString(driver?.driverMiddleName),
      LastName: this.getDefaultString(driver?.driverLastName),
      DriverDateOfBirth: this.getDefaultDate(driver?.driverDateOfBirth),
      Email: this.getDefaultString(email?.emailAddress),
      DriversLicenseNumber: this.getDefaultString(driver?.driversLicenseNumber),
      DriverLicenseExpiration: this.getDefaultDate(
        driver?.driversLicenseExpiresOn,
      ),
      DriverLicenseIssuedInStateCode: this.getDefaultString(
        driver?.driverLicenseIssuedInStateCode,
      ),
      PreferredUserName: driver
        ? this.getDefaultString(this.getDriverUsername(driver))
        : '',
      IsClearToTransport: this.getDefaultBoolean(driver?.isClearToTransport),
      IsInternal: this.getDefaultBoolean(driver?.isInternal),
      DriverState: this.getDefaultString(driver?.driverState),
      DriverPhone: this.getDefaultString(primaryPhone?.phoneNumber),
      DriverPhoneExtension: this.getDefaultString(primaryPhone?.phoneExtension),
      SecondaryDriverPhone: this.getDefaultString(secondaryPhone?.phoneNumber),
      SecondaryDriverPhoneExtension: this.getDefaultString(
        secondaryPhone?.phoneExtension,
      ),
      HasSMSConsent: this.getDefaultBoolean(primaryPhone?.hasSMSConsent),
      MessagingMethod: messagingMethod,
      EmergencyContactFirstName: this.getDefaultString(
        emergencyPhone?.contactFirstName,
      ),
      EmergencyContactLastName: this.getDefaultString(
        emergencyPhone?.contactLastName,
      ),
      EmergencyContactPhone: this.getDefaultString(emergencyPhone?.phoneNumber),
      EmergencyContactPhoneExtension: this.getDefaultString(
        emergencyPhone?.phoneExtension,
      ),
      IsActive: driver ? driver.driverState === 'active' : true,

      tripModes: [],
    };
  }

  public getDriverFromFormData(
    providerId: number,
    id: number | undefined,
    formData: any,
    emails: DriverEmailAddress[] | undefined,
    phoneNumbers: DriverPhoneNumber[] | undefined,
    roles: [],
  ): Driver {
    const email = emailUtils.getDriverEmail(emails);
    const primaryPhone = phoneUtils.getDriverPrimaryPhone(phoneNumbers);
    const secondaryPhone = phoneUtils.getDriverSecondaryPhone(phoneNumbers);
    const emergencyPhone = phoneUtils.getDriverEmergencyPhone(phoneNumbers);
    const driverDateOfBirth = DateTime.fromISO(formData.DriverDateOfBirth);
    const driverLicenseExpire = DateTime.fromISO(
      formData.DriverLicenseExpiration,
    );
    const newPhoneNumbers: DriverPhoneNumber[] = [
      {
        driverPhoneID: primaryPhone?.driverPhoneID,
        driverID: id,
        driverPhoneUseID: DriverPhoneUse.Driver,
        contactFirstName: formData.FirstName,
        contactLastName: formData.LastName,
        isPrimaryPhoneNumber: true,
        hasSMSConsent: formData.HasSMSConsent,
        driverPhoneState: 'active',
        phoneNumberTypeID: PhoneType.Mobile,
        phoneNumber: formData.DriverPhone.replace(/\s/g, ''),
        phoneExtension: formData.DriverPhoneExtension?.replace(/\D/g, ''),
      },
    ];
    if (
      formData.EmergencyContactPhone &&
      formData.EmergencyContactFirstName &&
      formData.EmergencyContactLastName
    ) {
      newPhoneNumbers.push({
        driverPhoneID: emergencyPhone?.driverPhoneID,
        driverID: id,
        driverPhoneUseID: DriverPhoneUse.Emergency,
        contactFirstName: formData.EmergencyContactFirstName,
        contactLastName: formData.EmergencyContactLastName,
        isPrimaryPhoneNumber: false,
        hasSMSConsent: formData.HasSMSConsent,
        driverPhoneState: 'active',
        phoneNumberTypeID: PhoneType.Mobile,
        phoneNumber: formData.EmergencyContactPhone.replace(/\s/g, ''),
        phoneExtension: formData.EmergencyContactPhoneExtension?.replace(
          /\D/g,
          '',
        ),
      });
    }
    if (formData.SecondaryDriverPhone) {
      newPhoneNumbers.push({
        driverPhoneID: secondaryPhone?.driverPhoneID,
        driverPhoneUseID: DriverPhoneUse.Driver,
        driverID: id,
        contactFirstName: formData.FirstName,
        contactLastName: formData.LastName,
        isPrimaryPhoneNumber: false,
        hasSMSConsent: formData.HasSMSConsent,
        driverPhoneState: 'active',
        phoneNumberTypeID: PhoneType.Mobile,
        phoneNumber: formData.SecondaryDriverPhone.replace(/\s/g, ''),
        phoneExtension: formData.SecondaryDriverPhoneExtension?.replace(
          /\D/g,
          '',
        ),
      });
    }
    const driver: Driver = {
      driverID: id,
      transportationProviderID: providerId,
      driverFirstName: formData.FirstName,
      driverMiddleName: formData.MiddleName,
      driverLastName: formData.LastName,
      preferredUserName: formData.PreferredUserName,

      userRoleIDs: roles,

      driverDateOfBirth: driverDateOfBirth.isValid
        ? driverDateOfBirth
            .setZone('utc')
            .toFormat(validationUtils.validDateFormat())
        : null,
      emailAddresses: [
        {
          driverEmailID: email?.driverEmailID,
          driverID: id,
          driverEmailUseID: DriverEmailUse.Primary,
          contactFirstName: formData.FirstName,
          contactLastName: formData.LastName,
          emailAddress: formData.Email,
          driverEmailState: 'active',
        },
      ],

      driversLicenseNumber: formData.DriversLicenseNumber
        ? formData.DriversLicenseNumber.toUpperCase()
        : null,
      driverLicenseIssuedInStateCode: formData.DriverLicenseIssuedInStateCode
        ? formData.DriverLicenseIssuedInStateCode
        : null,
      driversLicenseExpiresOn: driverLicenseExpire.isValid
        ? driverLicenseExpire
            .setZone('utc')
            .toFormat(validationUtils.validDateFormat())
        : '',
      messagingMethodID: formData.MessagingMethod,
      isClearToTransport: formData.IsClearToTransport,
      isInternal: formData.IsInternal,
      driverState: formData.IsActive ? 'active' : 'inactive',
      phoneNumbers: newPhoneNumbers,
    };

    return driver;
  }

  public getDriverUsername(driver: any) {
    return (
      driver?.userAccountDriver?.userAccount?.identityAccessCognito?.[0]
        ?.preferredUserName ||
      driver?.preferredUserName ||
      ''
    );
  }

  public resendEmailPayload(
    drivers: Driver[],
    state: number = 2,
    username: string | undefined,
    roles: [] | any,
  ) {
    return drivers.map((driver) => {
      const info = {
        driverID: driver?.driverID,
      };
      if (state === 1) {
        return {
          ...info,
          ...{
            userRoleIDs: roles,
          },
        };
      }
      return info;
    });
  }
}

const driverUtils = new DriverUtils();

export default driverUtils;
